import { Box, Flex, Image, Stack } from '@chakra-ui/react'
import { Body1, Body2, H1 } from 'components/typography'
import { ALPHABET } from 'constants/alphabet'
import { FC } from 'react'
import { useDeferMedia } from 'utils/ssrContent'

const defaultBulletPoints = [
  'Tratamentos sob medida, criados em parceria com seu médico(a) para atender suas necessidades individuais.',
  'Alcance resultados com soluções integradas: remédios manipulados, cursos, fitoterápicos e apoio de outros especialistas.',
  'Eleve a sua autoestima de uma forma prática com o melhor custo-benefício'
]

interface BulletPointsSectionProps {
  title?: string
  bulletPoints?: string[]
  isBanner?: boolean
  customList?: { itemChar?: string; itemText: string }[]
  /** Heading level of the title (same look); "h2" on pages that already have their H1. */
  titleAs?: 'h1' | 'h2'
  /**
   * Draw the photo as a lazy <img> with this alt text, placed exactly like the background image. Without it the
   * photo stays a CSS background.
   */
  photoAlt?: string
}

const PHOTO = '/landings/dermatology/aline_on_smartphone.webp'

const BulletPointsSection: FC<BulletPointsSectionProps> = ({
  title = 'Receba um tratamento elaborado só para você',
  bulletPoints = defaultBulletPoints,
  isBanner,
  titleAs = 'h1',
  photoAlt
}) => {
  const deferMedia = useDeferMedia()
  return (
    <Stack
      px={6}
      pb={{ base: 6, lg: '64px' }}
      pt={isBanner ? { base: 12, lg: 0 } : 0} // compensates for navbar
      align="center"
      {...(photoAlt
        ? { overflow: 'hidden' }
        : {
            // Below the fold: not in a lazy section's server HTML (it would download at once), see useDeferMedia
            backgroundImage: deferMedia ? undefined : PHOTO,
            backgroundSize: { base: 'auto', lg: '125%' },
            backgroundPosition: { base: '45% 75%', lg: 'center' }
          })}
      h="100vh" // TODO: compensate for navbar
      maxH={{ base: '930px', lg: '830px' }}
      justifyContent="space-between"
      pos="relative"
    >
      {photoAlt && (
        // background-size auto + position 45% 75% (natural size, no scaling) = object-fit none; from lg,
        // background-size 125% centered = 125% wide, centered
        <Image
          src={PHOTO}
          alt={photoAlt}
          htmlWidth={1440}
          htmlHeight={1080}
          loading="lazy"
          decoding="async"
          pos="absolute"
          top={{ base: 0, lg: '50%' }}
          left={{ base: 0, lg: '50%' }}
          w={{ base: '100%', lg: '125%' }}
          h={{ base: '100%', lg: 'auto' }}
          maxW="none"
          objectFit={{ base: 'none', lg: 'fill' }}
          objectPosition={{ base: '45% 75%', lg: 'center' }}
          transform={{ lg: 'translate(-50%, -50%)' }}
        />
      )}
      {/* The photo is bright where the title sits (the phone screen), so darken the top for the light title */}
      <Box
        pos="absolute"
        top={0}
        left={0}
        w="full"
        h={{ base: '40%', lg: '45%' }}
        bgGradient="linear(to-b, blackAlpha.700, transparent)"
        pointerEvents="none"
      />
      <H1
        as={titleAs}
        color="grayscale.20"
        maxW={{ base: '540px', lg: '600px' }}
        zIndex={1}
        pt={{ base: '96px', lg: '104px' }} // clears the floating mobile navbar
        textAlign="center"
      >
        {title}
      </H1>
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        p={{ base: 6, lg: 14 }}
        gap={10}
        bg="rgba(246, 244, 232, 0.10)"
        backdropFilter="blur(56px)"
        borderRadius={{ base: '24px', lg: '40px' }}
        maxW={{ base: '400px', lg: '1280px' }}
        pos="relative"
        zIndex={1}
      >
        {bulletPoints.map((bulletPoint, index) => (
          <Flex key={bulletPoint} h="full">
            <Box>
              <Flex
                justifyContent="center"
                alignItems="center"
                boxSize="28px"
                border="2px"
                borderRadius="full"
                color="grayscale.20"
              >
                <Body2 color="grayscale.20">{ALPHABET[index].toUpperCase()}</Body2>
              </Flex>
            </Box>

            <Box ml="1rem" my="auto">
              <Body1 color="grayscale.20">{bulletPoint}</Body1>
            </Box>
          </Flex>
        ))}
      </Flex>
    </Stack>
  )
}

export default BulletPointsSection
